import { useState } from "react";
import { CalendarClock, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { cdrErr, fmtWhen } from "./cdrMeta";

function LastAuto({ last }) {
  if (!last) return <span data-testid="cdr-auto-last">Has not sent automatically yet.</span>;
  return <span data-testid="cdr-auto-last">Last automatic check {fmtWhen(last.at)} — <b>{last.status}</b>{last.note ? ` · ${last.note}` : ""}</span>;
}

/** The automatic monthly send switch (super admins), plus who gets a copy and who replies go to. */
export function CdrAutoCard({ ov, reload }) {
  const c = ov.config;
  const [f, setF] = useState({ bcc: c.bcc.join(", "), reply_to: c.reply_to || "" });
  const [ask, setAsk] = useState(false);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async (auto = c.auto_enabled) => {
    setBusy(true);
    try {
      await api.put("/cdr-dispatch/config", { auto_enabled: auto, bcc: f.bcc.split(/[,;\s]+/).filter(Boolean), reply_to: f.reply_to.trim() || null });
      toast.success(auto !== c.auto_enabled ? `Automatic sending is ${auto ? "ON" : "off"}` : "Saved");
      setAsk(false); setOk(false); reload();
    } catch (e) { toast.error(cdrErr(e, "Could not save")); } finally { setBusy(false); }
  };
  const onToggle = (v) => (v ? setAsk(true) : save(false));
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="cdr-auto-card">
      <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><CalendarClock className="h-4 w-4" /></div>
        <div className="flex-1">
          <h3 className="font-heading text-sm font-semibold text-slate-800">Automatic monthly send <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${c.auto_enabled ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-700"}`} data-testid="cdr-auto-state">{c.auto_enabled ? "ON" : "OFF"}</span></h3>
          <p className="text-xs text-slate-500">On the 1st at 8:30 AM Mountain, every enabled recipient gets the cycle that just closed. If the cycle's last day is not in the billing database yet it waits and retries daily until the 7th, then alerts the billing mailbox. <LastAuto last={c.last_auto} /></p>
        </div>
        <Switch checked={c.auto_enabled} onCheckedChange={onToggle} disabled={!ov.is_super || busy} data-testid="cdr-auto-toggle" />
      </div>
      <div className="grid gap-3 px-5 py-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
        <label className="space-y-1"><span className="text-xs font-medium text-slate-600">Copy every e-mail to (BCC)</span>
          <Input value={f.bcc} onChange={(e) => setF({ ...f, bcc: e.target.value })} disabled={!ov.can_edit} className="h-9 text-sm" data-testid="cdr-auto-bcc" /></label>
        <label className="space-y-1"><span className="text-xs font-medium text-slate-600">Replies go to</span>
          <Input value={f.reply_to} onChange={(e) => setF({ ...f, reply_to: e.target.value })} disabled={!ov.can_edit} className="h-9 text-sm" data-testid="cdr-auto-reply-to" /></label>
        {ov.can_edit && <Button onClick={() => save()} disabled={busy} data-testid="cdr-auto-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</Button>}
      </div>
      {!ov.is_super && <p className="px-5 pb-3 text-[11px] text-slate-500">Only super admins can switch automatic sending on or off.</p>}
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent data-testid="cdr-auto-dialog">
          <AlertDialogHeader><AlertDialogTitle>Send CDR files automatically?</AlertDialogTitle>
            <AlertDialogDescription>From the next 1st, {ov.recipients.filter((r) => r.enabled).length} enabled recipients get real e-mails with their calls for the cycle that just closed.</AlertDialogDescription></AlertDialogHeader>
          <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="cdr-auto-confirm" /> I checked the recipient list and a test send.</label>
          <AlertDialogFooter><AlertDialogCancel data-testid="cdr-auto-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); save(true); }} disabled={!ok || busy} data-testid="cdr-auto-apply">Switch on</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
